<template>
  <teleport to="body">
    <transition name="confirm-fade">
      <div
        v-if="showModal"
        class="confirm-overlay"
        v-on:click.self="dismiss"
      >
        <transition name="confirm-scale" appear>
          <div
            v-if="showModal"
            class="confirm-panel"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
            :aria-describedby="messageId"
          >
            <header class="confirm-head">
              <div class="confirm-head-text">
                <span class="confirm-eyebrow">
                  <font-awesome-icon icon="fa-solid fa-phone" />
                  One last step
                </span>
                <h2 :id="titleId">Add your phone number</h2>
              </div>
              <button
                type="button"
                class="confirm-close"
                v-on:click="dismiss"
                :disabled="isSaving"
                aria-label="Close"
              >
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </header>

            <form class="confirm-body" v-on:submit.prevent="save">
              <p class="confirm-question" :id="messageId">
                We use your phone number to reach you and for support communications.
              </p>

              <label class="field">
                <span>Phone Number</span>
                <div class="phone-row">
                  <select
                    v-model="selectedCountryCode"
                    class="country-code-select"
                    aria-label="Country code"
                    :disabled="isSaving"
                  >
                    <option
                      v-for="country in countryCodes"
                      :key="country.iso"
                      :value="country.code"
                    >
                      {{ country.label }}
                    </option>
                  </select>
                  <input
                    ref="phoneInput"
                    v-model.trim="phoneLocalNumber"
                    type="tel"
                    placeholder="Phone number"
                    autocomplete="tel-national"
                    :disabled="isSaving"
                    v-on:input="onPhoneInput"
                  />
                </div>
                <small v-if="phoneError" class="form-text text-danger">{{ phoneError }}</small>
              </label>
            </form>

            <footer class="confirm-foot">
              <button
                type="button"
                class="confirm-btn confirm-btn-secondary"
                v-on:click="dismiss"
                :disabled="isSaving"
              >
                Maybe later
              </button>
              <button
                type="button"
                class="confirm-btn confirm-btn-primary"
                v-on:click="save"
                :disabled="isSaving"
              >
                <span v-if="isSaving" class="confirm-spinner" aria-hidden="true"></span>
                <span>{{ isSaving ? 'Saving...' : 'Save' }}</span>
              </button>
            </footer>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { UPDATE_ACCOUNT } from "@/store/action_types";
import { TOAST_GLOBAL_CONFIG } from "@/utils/const";
import { COUNTRY_CODES, normalizePhoneNumber } from "@/utils/phone";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPhone, faXmark } from "@fortawesome/free-solid-svg-icons";

library.add(faPhone, faXmark);

let uid = 0;

export default {
  name: "PhoneNumberPromptModal",
  emits: ["dismiss", "saved"],
  data() {
    uid += 1;
    return {
      showModal: true,
      isSaving: false,
      countryCodes: COUNTRY_CODES,
      selectedCountryCode: "+251",
      phoneLocalNumber: "",
      phoneError: "",
      titleId: `phone-prompt-title-${uid}`,
      messageId: `phone-prompt-message-${uid}`,
      previousBodyOverflow: "",
    };
  },
  mounted() {
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.$nextTick(() => {
      if (this.$refs.phoneInput && typeof this.$refs.phoneInput.focus === "function") {
        this.$refs.phoneInput.focus();
      }
    });
  },
  beforeUnmount() {
    document.body.style.overflow = this.previousBodyOverflow;
  },
  methods: {
    onPhoneInput() {
      if (this.phoneError) this.phoneError = "";
    },
    dismiss() {
      if (this.isSaving) return;
      this.$emit("dismiss");
    },
    save() {
      if (this.isSaving) return;

      const local = (this.phoneLocalNumber || "").trim();
      if (!local) {
        this.phoneError = "Enter your phone number.";
        return;
      }

      const phoneNumber = normalizePhoneNumber(this.selectedCountryCode, local);
      if (phoneNumber.replace(/\D/g, "").length < 8) {
        this.phoneError = "Enter a valid phone number.";
        return;
      }

      this.isSaving = true;
      this.$store
        .dispatch(UPDATE_ACCOUNT, { phoneNumber })
        .then((res) => {
          if (res) {
            this.$toast.success("Phone number saved.", TOAST_GLOBAL_CONFIG);
            this.$emit("saved");
          }
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>

<style scoped>
.confirm-overlay {
  position: fixed;
  inset: 0;
  /* Below the toast container (.v-toast, z-index 1090) so error/success
     toasts stay visible above the prompt — matches the app's other modals. */
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.confirm-panel {
  width: 100%;
  max-width: 440px;
  background: var(--ehs-color-surface);
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22), 0 4px 12px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-sans);
}

.confirm-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 22px 16px;
  border-bottom: 1px solid var(--ehs-color-border);
}

.confirm-head-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.confirm-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: var(--ehs-radius-pill);
  background: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary-active);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.confirm-eyebrow .svg-inline--fa {
  font-size: 0.7rem;
}

.confirm-head h2 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--ehs-color-ink);
}

.confirm-close {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-surface);
  border-radius: var(--ehs-radius-sm);
  color: var(--ehs-color-muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  flex-shrink: 0;
}

.confirm-close:hover:not(:disabled) {
  background: var(--ehs-color-canvas);
  color: var(--ehs-color-ink);
  border-color: var(--ehs-color-border-strong);
}

.confirm-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.confirm-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 22px;
}

.confirm-question {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 500;
  color: var(--ehs-color-text);
  line-height: 1.5;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field > span {
  color: var(--ehs-color-muted);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.phone-row {
  display: grid;
  grid-template-columns: minmax(110px, 0.4fr) minmax(0, 1fr);
  gap: 10px;
}

.field input,
.phone-row input,
.phone-row select {
  padding: 10px 12px;
  border: 1px solid var(--ehs-color-border);
  border-radius: 10px;
  background: var(--ehs-color-surface);
  color: var(--ehs-color-ink);
  font-size: 0.86rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.field input:focus,
.phone-row input:focus,
.phone-row select:focus {
  border-color: var(--ehs-color-primary);
  box-shadow: 0 0 0 3px rgba(115, 196, 29, 0.18);
}

.country-code-select {
  padding-left: 10px !important;
  padding-right: 10px !important;
}

.form-text.text-danger {
  margin-top: 2px;
  color: var(--ehs-color-danger);
  font-size: 0.74rem;
  font-weight: 500;
}

.confirm-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px 18px;
  border-top: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-canvas);
}

.confirm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 96px;
  padding: 0.55rem 1rem;
  border-radius: var(--ehs-radius-md);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.1s ease;
}

.confirm-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.confirm-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.confirm-btn-secondary {
  background: var(--ehs-color-surface);
  border: 1px solid var(--ehs-color-border);
  color: var(--ehs-color-ink);
}

.confirm-btn-secondary:hover:not(:disabled) {
  background: var(--ehs-color-canvas-strong);
  border-color: var(--ehs-color-border-strong);
}

.confirm-btn-primary {
  background: var(--ehs-color-primary);
  border: 1px solid var(--ehs-color-primary);
  color: #ffffff;
}

.confirm-btn-primary:hover:not(:disabled) {
  background: var(--ehs-color-primary-hover);
  border-color: var(--ehs-color-primary-hover);
}

.confirm-btn-primary:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(115, 196, 29, 0.25);
}

.confirm-spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  animation: confirm-spin 0.6s linear infinite;
}

@keyframes confirm-spin {
  to { transform: rotate(360deg); }
}

.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.18s ease;
}

.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}

.confirm-scale-enter-active,
.confirm-scale-leave-active {
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s ease;
}

.confirm-scale-enter-from,
.confirm-scale-leave-to {
  transform: scale(0.94) translateY(6px);
  opacity: 0;
}

@media (max-width: 480px) {
  .confirm-panel {
    max-width: 100%;
  }
  .phone-row {
    grid-template-columns: 1fr;
  }
  .confirm-foot {
    flex-direction: column-reverse;
  }
  .confirm-btn {
    width: 100%;
  }
}
</style>
